import PropTypes from "prop-types";
import React, { useState, useEffect, useCallback } from "react";
import { Box, H5, Divider } from "orcs-design-system";
import { useHistory } from "react-router-dom";
import { uniqBy, sortBy, get, isEmpty } from "lodash";

import TreeView from "src/shared/TreeView";
import urlPaths, { url, matchPaths } from "src/consts/urlPaths";
import TagTreeHeader from "src/components/TagsNavigation/TagTreeHeader";
import { pagedSearchTags } from "src/queries/tags.graphql";
import { TAG_LOAD_LIMIT } from "src/util/consts";
import { useWorkspaceTagConfig } from "src/contexts/global/WorkspaceContext";
import useLazyLoad from "src/hooks/useLazyLoad";
import { reportError } from "src/services/errorReporting";
import buildTagTypeTree from "./buildTagTypeTree";

const getUniqueTags = (tags) => {
  return sortBy(uniqBy(tags, "id"), "displayValue");
};

const isTagType = (tagTypesConfig, nodeId) => {
  const tagTypeConfig = get(tagTypesConfig, nodeId, {});
  return !isEmpty(tagTypeConfig);
};

const customizeTreeHeader = (props) => {
  const WrappedTreeHeader = ({ node }) => {
    return <TagTreeHeader {...props} node={node} />;
  };

  WrappedTreeHeader.propTypes = {
    node: PropTypes.object.isRequired,
  };

  return WrappedTreeHeader;
};

const TagTypeNavigation = ({ tagType }) => {
  const [treeData, setTreeData] = useState([]);
  const [childrenLoaded, setChildrenLoaded] = useState({});
  const [allTags, setAllTags] = useState({});
  const [toggledNodes, setToggledNodes] = useState({});
  const activeTagId = matchPaths()?.params?.id;
  const history = useHistory();
  const tagTypesConfig = useWorkspaceTagConfig();

  const loadTags = useLazyLoad({
    query: pagedSearchTags,
    additionalVariables: {
      showAppliedCount: true,
    },
  });

  const loadChildTags = useCallback(
    async (node) => {
      const { id: nodeId, name } = node;
      if (!nodeId) {
        return null;
      }

      const currentTags = allTags[nodeId]?.tags || [];
      const filter = isTagType(tagTypesConfig, nodeId)
        ? { tagTypes: [nodeId] }
        : { attribute: { type: "parentTag", value: nodeId } };

      try {
        const data = await loadTags({
          input: {
            filter,
            offset: 0,
            limit: TAG_LOAD_LIMIT,
          },
        });

        const newTags = data?.results?.tags || [];
        const totalCount = data?.results?.totalCount || 0;

        React.startTransition(() => {
          setAllTags((prev) => ({
            ...prev,
            [nodeId]: {
              tags: getUniqueTags([...currentTags, ...newTags]),
              totalCount,
              name: isTagType(tagTypesConfig, nodeId)
                ? get(tagTypesConfig, [nodeId, "name"], nodeId)
                : name,
            },
          }));
          setChildrenLoaded((prev) => ({ ...prev, [nodeId]: true }));
        });

        return {
          tags: newTags,
          totalCount,
        };
      } catch (error) {
        reportError(error, {
          context: "TagTypeNavigation",
          action: "loadChildTags",
          nodeId,
        });
        return null;
      }
    },
    [loadTags, allTags, tagTypesConfig]
  );

  // Update tree when tags or loading state changes
  useEffect(() => {
    if (!tagType?.id) {
      return;
    }

    const data = buildTagTypeTree({
      nodeId: tagType.id,
      tags: { ...allTags },
      activeTagId,
      node: tagType,
      childrenLoaded: { ...childrenLoaded },
      toggledNodes: { ...toggledNodes },
    });

    setTreeData(data);
  }, [
    activeTagId,
    allTags,
    tagType,
    toggledNodes,
    tagTypesConfig,
    childrenLoaded,
  ]);

  const onNodeToggled = useCallback(
    async (node) => {
      const { id, toggled } = node;

      setToggledNodes((prev) => ({
        ...prev,
        [id]: toggled,
      }));

      if (toggled) {
        return loadChildTags(node);
      }
      return null;
    },
    [loadChildTags]
  );

  const onNodeSelected = useCallback(
    async (node) => {
      const { id } = node;
      const loadedData = await onNodeToggled({ ...node, toggled: true });
      if (isEmpty(loadedData?.tags) && !isTagType(tagTypesConfig, node?.id)) {
        history.push(url(urlPaths.TAG_DETAILS, { id }));
      } else {
        history.push(url(urlPaths.TAG_EXPLORE, { id }));
      }
    },
    [history, onNodeToggled, tagTypesConfig]
  );

  return (
    <Box data-testid={`${tagType.id}-navigation`} mb="r">
      <H5 fontWeight="bold" data-testid="heading">
        {tagType.name}
      </H5>
      <Box my="r">
        <Divider light />
      </Box>
      {!treeData.length && <H5>No tags data available</H5>}
      <TreeView
        data={treeData}
        onNodeSelected={onNodeSelected}
        onNodeToggled={onNodeToggled}
        customizeTreeHeader={customizeTreeHeader}
        onToggle={onNodeToggled}
      />
    </Box>
  );
};

TagTypeNavigation.propTypes = {
  tagType: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
};

export default React.memo(TagTypeNavigation);
